import React from "react";
import { Link } from "react-router-dom";
import { trackCtaClicked } from "../analytics/analytics";
import "bootstrap-icons/font/bootstrap-icons.css";

const variantClass = {
  default:
    "border-slate-900/10 bg-gray/70 text-slate-900 backdrop-blur-md dark:border-white/15 dark:bg-slate-950/40 dark:text-slate-900/95",
  emphasis:
    "border-red-900/15 bg-red-100 text-slate-900  dark:border-white/20 dark:bg-gray/10 dark:text-slate-900/95",
  muted:
    "border-slate-200/80 bg-slate-50/90 text-slate-800 backdrop-blur-sm dark:border-white/10 dark:bg-gray/5 dark:text-slate-900/90",
};

/**
 * Reusable top-of-page alert / callout with optional primary action on the right.
 *
 * @param {'default' | 'emphasis' | 'muted'} [variant]
 * @param {string} [iconClass] — Bootstrap Icons name without `bi-` prefix (e.g. `building`)
 * @param {string} title
 * @param {React.ReactNode} [children] — description body
 * @param {{ label: string, cta?: string, onClick?: () => void, to?: string }} [action] — `to` uses react-router Link
 * @param {string} [className]
 */
export default function AlertBanner({
  variant = "default",
  iconClass = "info-circle",
  title,
  children,
  action,
  className = "",
}) {
  const shell = variantClass[variant] ?? variantClass.default;
  const iconBi = iconClass.startsWith("bi ") ? iconClass : `bi bi-${iconClass}`;

  const handleActionClick = (handler) => {
    if (action?.cta) trackCtaClicked(action.cta);
    handler?.();
  };

  const actionEl =
    action &&
    (action.to ? (
      <Link
        to={action.to}
        onClick={() => handleActionClick()}
        className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-slate-200 px-4 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-800 dark:bg-gray dark:text-slate-900 dark:hover:bg-gray/90"
      >
        {action.label}
        <i className="bi bi-arrow-right-short text-lg leading-none" aria-hidden />
      </Link>
    ) : (
      <button
        type="button"
        onClick={() => handleActionClick(action.onClick)}
        className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-white-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-gray dark:text-slate-900 dark:hover:bg-gray/90"
      >
        {action.label}
        <i className="bi bi-arrow-right-short text-lg leading-none" aria-hidden />
      </button>
    ));

  return (
    <div
      role="status"
      className={`mb-5 flex flex-col gap-4 rounded-2xl border border-black px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-5 sm:py-4 ${shell} ${className}`.trim()}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span
          className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900/[0.08] text-slate-900 dark:bg-gray/15 dark:text-slate-900"
          aria-hidden
        >
          <i className={`${iconBi} text-lg`} />
        </span>
        <div className="min-w-0 pt-0.5">
          <p className="text-sm font-semibold tracking-tight sm:text-base">{title}</p>
          {children ? (
            <div className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-900/70">{children}</div>
          ) : null}
        </div>
      </div>
      {actionEl}
    </div>
  );
}
